import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen bg-[#0B1120] flex items-center justify-center px-6"
    >
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm bg-[#0F172A] border border-white/10 p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="w-10 h-10 bg-[#ED1C24] flex items-center justify-center font-display font-bold text-white text-lg -skew-x-6">
            K
          </span>
          <div>
            <p className="font-display font-semibold text-white">KINOS Admin</p>
            <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
              Restricted access
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <Input
            data-testid="admin-email-input"
            type="email"
            required
            placeholder="Admin email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none h-12"
          />
          <Input
            data-testid="admin-password-input"
            type="password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none h-12"
          />
        </div>

        {error && (
          <p data-testid="admin-login-error" className="text-[#ED1C24] text-xs mt-4">
            {error}
          </p>
        )}

        <button
          data-testid="admin-login-submit-btn"
          type="submit"
          disabled={loading}
          className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white font-medium py-3.5 transition-colors duration-300"
        >
          <Lock size={15} />
          {loading ? "Signing in..." : "Sign In"}
        </button>

        <button
          type="button"
          data-testid="forgot-password-link"
          onClick={() => navigate("/admin/forgot-password")}
          className="w-full mt-4 text-xs text-slate-500 hover:text-[#FFC61A] transition-colors duration-300"
        >
          Forgot password?
        </button>
      </form>
    </main>
  );
}
